/**
 * Profile trait registry. The single source of truth that maps every
 * field on `ProfileSchema` to its category, kind, options, and visual
 * treatment.
 *
 * Adding a new trait:
 *   1. Add the field to ProfileSchema + the DB schema + a migration.
 *   2. Add an entry below.
 *   3. The wizard, profile view, and profile edit pages pick it up
 *      automatically.
 *
 * The registry is intentionally code-defined — admin runtime
 * additions land in a separate `trait_option_overrides` table later
 * (plan §Phase 7 admin) and only apply to **non-hard-filter** traits.
 */

import { EDUCATION_DISCIPLINE_OPTIONS } from './options/education-disciplines.js';
import { ETHNICITY_OPTIONS } from './options/ethnicities.js';
import { INTEREST_OPTIONS } from './options/interests.js';
import { LANGUAGE_OPTIONS } from './options/languages.js';
import { PERSONALITY_OPTIONS } from './options/personality.js';
import { PROFESSION_OPTIONS } from './options/professions.js';
import type { Trait, TraitOption } from './types.js';

/** Helper to build an option list from a plain `{value, labelKey,
 *  icon}` shape — keeps the registry compact and readable. */
function options(...opts: readonly TraitOption[]): readonly TraitOption[] {
  return opts;
}

// ─── Enum-based option lists, expressed inline for compactness ────
// Source: packages/shared/src/enums.ts. When an enum gains a value,
// add it here too. Both sides are checked by registry.test.ts.

const SECT_OPTIONS = options(
  { value: 'sunni', labelKey: 'sect.sunni', icon: 'moon-star' },
  { value: 'shia', labelKey: 'sect.shia', icon: 'moon-star' },
);

/** Sub-sect / school. `na` is the explicit non-disclosure; `other`
 *  is the escape hatch. Order is informational not preference. */
const SUB_SECT_OPTIONS = options(
  { value: 'na', labelKey: 'subSect.na' },
  { value: 'hanafi', labelKey: 'subSect.hanafi' },
  { value: 'shafii', labelKey: 'subSect.shafii' },
  { value: 'maliki', labelKey: 'subSect.maliki' },
  { value: 'hanbali', labelKey: 'subSect.hanbali' },
  { value: 'twelver', labelKey: 'subSect.twelver' },
  { value: 'ismaili', labelKey: 'subSect.ismaili' },
  { value: 'zaidi', labelKey: 'subSect.zaidi' },
  { value: 'ibadi', labelKey: 'subSect.ibadi' },
  { value: 'sufi', labelKey: 'subSect.sufi' },
  { value: 'salafi', labelKey: 'subSect.salafi' },
  { value: 'other', labelKey: 'subSect.other' },
);

const PRAYER_OPTIONS = options(
  { value: '5x_daily', labelKey: 'prays.5x_daily', icon: 'check-check' },
  { value: 'mostly', labelKey: 'prays.mostly', icon: 'check' },
  { value: 'sometimes', labelKey: 'prays.sometimes', icon: 'minus' },
  { value: 'rarely', labelKey: 'prays.rarely', icon: 'minus' },
  { value: 'never', labelKey: 'prays.never', icon: 'x' },
);

const FASTING_OPTIONS = options(
  { value: 'always', labelKey: 'fasts.always', icon: 'check-check' },
  { value: 'mostly', labelKey: 'fasts.mostly', icon: 'check' },
  { value: 'sometimes', labelKey: 'fasts.sometimes', icon: 'minus' },
  { value: 'rarely', labelKey: 'fasts.rarely', icon: 'minus' },
  { value: 'never', labelKey: 'fasts.never', icon: 'x' },
);

const QURAN_OPTIONS = options(
  { value: 'daily', labelKey: 'quran.daily', icon: 'book-open' },
  { value: 'weekly', labelKey: 'quran.weekly', icon: 'book-open' },
  { value: 'monthly', labelKey: 'quran.monthly', icon: 'book' },
  { value: 'sometimes', labelKey: 'quran.sometimes', icon: 'book' },
  { value: 'rarely', labelKey: 'quran.rarely', icon: 'book' },
  { value: 'never', labelKey: 'quran.never', icon: 'x' },
);

const DIET_OPTIONS = options(
  { value: 'halal_strict', labelKey: 'diet.halal_strict', icon: 'check-check' },
  { value: 'halal_flexible', labelKey: 'diet.halal_flexible', icon: 'check' },
  { value: 'vegetarian', labelKey: 'diet.vegetarian', icon: 'salad' },
  { value: 'vegan', labelKey: 'diet.vegan', icon: 'sprout' },
  { value: 'pescatarian', labelKey: 'diet.pescatarian', icon: 'fish' },
  { value: 'no_restriction', labelKey: 'diet.no_restriction', icon: 'utensils' },
);

const ALCOHOL_OPTIONS = options(
  { value: 'never', labelKey: 'alcohol.never', icon: 'x' },
  { value: 'quit', labelKey: 'alcohol.quit', icon: 'undo-2' },
  { value: 'sometimes', labelKey: 'alcohol.sometimes', icon: 'minus' },
  { value: 'regular', labelKey: 'alcohol.regular', icon: 'wine' },
);

const SMOKING_OPTIONS = options(
  { value: 'never', labelKey: 'smoking.never', icon: 'x' },
  { value: 'ex', labelKey: 'smoking.ex', icon: 'undo-2' },
  { value: 'sometimes', labelKey: 'smoking.sometimes', icon: 'minus' },
  { value: 'regular', labelKey: 'smoking.regular', icon: 'cigarette' },
);

const CONVERT_OPTIONS = options(
  { value: 'born_muslim', labelKey: 'convert.born_muslim', icon: 'heart' },
  { value: 'revert', labelKey: 'convert.revert', icon: 'compass' },
);

const MARITAL_OPTIONS = options(
  { value: 'never_married', labelKey: 'marital.never_married', icon: 'circle' },
  { value: 'divorced', labelKey: 'marital.divorced', icon: 'split' },
  { value: 'widowed', labelKey: 'marital.widowed', icon: 'cloud' },
);

// Audit Spec C5 — richer custody texture (see ChildrenCustody enum).
const CHILDREN_CUSTODY_OPTIONS = options(
  { value: 'lives_with_me', labelKey: 'custody.lives_with_me', icon: 'shield' },
  { value: 'shared_custody', labelKey: 'custody.shared_custody', icon: 'shield-half' },
  { value: 'weekend_or_holiday', labelKey: 'custody.weekend_or_holiday', icon: 'shield-half' },
  { value: 'international', labelKey: 'custody.international', icon: 'globe' },
  { value: 'other_parent', labelKey: 'custody.other_parent', icon: 'circle' },
  { value: 'prefer_not_to_say', labelKey: 'custody.prefer_not_to_say', icon: 'minus' },
);

const EDUCATION_OPTIONS = options(
  { value: 'high_school', labelKey: 'education.high_school', icon: 'graduation-cap' },
  { value: 'vocational', labelKey: 'education.vocational', icon: 'hard-hat' },
  { value: 'bachelors', labelKey: 'education.bachelors', icon: 'graduation-cap' },
  { value: 'masters', labelKey: 'education.masters', icon: 'graduation-cap' },
  { value: 'phd', labelKey: 'education.phd', icon: 'award' },
  { value: 'other', labelKey: 'education.other', icon: 'book' },
);

const MARRIAGE_TIMEFRAME_OPTIONS = options(
  { value: 'within_6mo', labelKey: 'marriage_timeframe.within_6mo', icon: 'clock' },
  { value: '6_to_12mo', labelKey: 'marriage_timeframe.6_to_12mo', icon: 'clock' },
  { value: '1_to_2yr', labelKey: 'marriage_timeframe.1_to_2yr', icon: 'calendar' },
  { value: 'exploring', labelKey: 'marriage_timeframe.exploring', icon: 'compass' },
);

const FAMILY_INVOLVEMENT_OPTIONS = options(
  { value: 'immediately', labelKey: 'family_involvement.immediately', icon: 'users' },
  {
    value: 'after_we_connect',
    labelKey: 'family_involvement.after_we_connect',
    icon: 'message-circle',
  },
  { value: 'after_we_meet', labelKey: 'family_involvement.after_we_meet', icon: 'coffee' },
  {
    value: 'when_we_both_agree',
    labelKey: 'family_involvement.when_we_both_agree',
    icon: 'handshake',
  },
  { value: 'case_by_case', labelKey: 'family_involvement.case_by_case', icon: 'shuffle' },
);

const WANTS_CHILDREN_OPTIONS = options(
  { value: 'definitely', labelKey: 'wants_children.definitely', icon: 'baby' },
  { value: 'wants', labelKey: 'wants_children.wants', icon: 'baby' },
  { value: 'open', labelKey: 'wants_children.open', icon: 'circle' },
  { value: 'no', labelKey: 'wants_children.no', icon: 'x' },
);

const RELOCATION_OPTIONS = options(
  { value: 'will', labelKey: 'relocation.will', icon: 'plane' },
  { value: 'wont', labelKey: 'relocation.wont', icon: 'home' },
  { value: 'negotiable', labelKey: 'relocation.negotiable', icon: 'shuffle' },
);

const LIVING_ARRANGEMENT_OPTIONS = options(
  { value: 'own_home', labelKey: 'living_arrangement.own_home', icon: 'home' },
  { value: 'with_parents', labelKey: 'living_arrangement.with_parents', icon: 'users' },
  {
    value: 'extended_family',
    labelKey: 'living_arrangement.extended_family',
    icon: 'users',
  },
  { value: 'negotiable', labelKey: 'living_arrangement.negotiable', icon: 'shuffle' },
);

const POLYGAMY_PREFERENCE_OPTIONS = options(
  { value: 'monogamy_only', labelKey: 'polygamy.monogamy_only', icon: 'heart' },
  { value: 'open', labelKey: 'polygamy.open', icon: 'users' },
  { value: 'negotiable', labelKey: 'polygamy.negotiable', icon: 'shuffle' },
  { value: 'currently_married', labelKey: 'polygamy.currently_married', icon: 'ring' },
  {
    value: 'prefer_not_to_say',
    labelKey: 'polygamy.prefer_not_to_say',
    icon: 'circle',
  },
);

const GENDER_OPTIONS = options(
  { value: 'male', labelKey: 'gender.male' },
  { value: 'female', labelKey: 'gender.female' },
);

// Dress style is gender-dependent. The wizard/edit form selects the
// option set at runtime based on the user's gender; both lists are
// exported so the registry knows about both.
export const DRESS_WOMAN_OPTIONS = options(
  { value: 'niqab', labelKey: 'dress_woman.niqab' },
  { value: 'jilbab', labelKey: 'dress_woman.jilbab' },
  { value: 'hijab', labelKey: 'dress_woman.hijab' },
  { value: 'modest_no_hijab', labelKey: 'dress_woman.modest_no_hijab' },
  { value: 'casual', labelKey: 'dress_woman.casual' },
  { value: 'dont_dress_religiously', labelKey: 'dress_woman.dont_dress_religiously' },
  { value: 'prefer_not_say', labelKey: 'dress_woman.prefer_not_say' },
);

export const DRESS_MAN_OPTIONS = options(
  { value: 'thobe_regular', labelKey: 'dress_man.thobe_regular' },
  { value: 'thobe_occasional', labelKey: 'dress_man.thobe_occasional' },
  { value: 'beard_full', labelKey: 'dress_man.beard_full' },
  { value: 'beard_trim', labelKey: 'dress_man.beard_trim' },
  { value: 'casual', labelKey: 'dress_man.casual' },
  { value: 'dont_dress_religiously', labelKey: 'dress_man.dont_dress_religiously' },
  { value: 'prefer_not_say', labelKey: 'dress_man.prefer_not_say' },
);

// ─── The registry ────────────────────────────────────────────────

export const PROFILE_TRAITS: readonly Trait[] = [
  // ─── Basics ────────────────────────────────────────────────────
  {
    field: 'displayName',
    labelKey: 'trait.displayName',
    category: 'basics',
    order: 1,
    kind: 'text',
    maxLength: 40,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'plain-text',
  },
  {
    field: 'dateOfBirth',
    labelKey: 'trait.dateOfBirth',
    category: 'basics',
    order: 2,
    kind: 'date',
    hardFilter: true, // age window in match-service
    editableAfterWizard: false, // plan §Account Lifecycle Edge Cases
    required: true,
    display: 'plain-text',
  },
  {
    field: 'gender',
    labelKey: 'trait.gender',
    category: 'basics',
    order: 3,
    kind: 'select',
    options: GENDER_OPTIONS,
    hardFilter: true,
    editableAfterWizard: false, // immutable
    required: true,
    display: 'chip',
  },
  {
    field: 'heightCm',
    labelKey: 'trait.heightCm',
    category: 'basics',
    order: 4,
    kind: 'number',
    range: { min: 120, max: 230 },
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'plain-text',
  },
  {
    field: 'maritalStatus',
    labelKey: 'trait.maritalStatus',
    category: 'family',
    order: 1,
    kind: 'select',
    options: MARITAL_OPTIONS,
    hardFilter: true,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'profession',
    labelKey: 'trait.profession',
    category: 'basics',
    order: 5,
    kind: 'select',
    options: PROFESSION_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'educationLevel',
    labelKey: 'trait.educationLevel',
    category: 'basics',
    order: 6,
    kind: 'select',
    options: EDUCATION_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'educationDiscipline',
    labelKey: 'trait.educationDiscipline',
    helpKey: 'trait.educationDiscipline.help',
    category: 'basics',
    order: 7,
    kind: 'select',
    options: EDUCATION_DISCIPLINE_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: false,
    display: 'chip',
  },

  // ─── Background ────────────────────────────────────────────────
  {
    field: 'nationalityIso',
    labelKey: 'trait.nationalityIso',
    category: 'background',
    order: 1,
    kind: 'country',
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'countryOfResidenceIso',
    labelKey: 'trait.countryOfResidenceIso',
    category: 'background',
    order: 2,
    kind: 'country',
    hardFilter: true, // segment_countries on promotional events + geo filter
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'grewUpCountryIso',
    labelKey: 'trait.grewUpCountryIso',
    category: 'background',
    order: 3,
    kind: 'country',
    hardFilter: false,
    editableAfterWizard: true,
    required: false,
    display: 'chip',
  },
  {
    field: 'grewUpRegion',
    labelKey: 'trait.grewUpRegion',
    category: 'background',
    order: 4,
    kind: 'text',
    maxLength: 80,
    hardFilter: false,
    editableAfterWizard: true,
    required: false,
    display: 'plain-text',
  },
  {
    field: 'ethnicities',
    labelKey: 'trait.ethnicities',
    category: 'background',
    order: 5,
    kind: 'multi-select',
    options: ETHNICITY_OPTIONS,
    multiBounds: { min: 1, max: 5 },
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip-list',
  },
  {
    field: 'languages',
    labelKey: 'trait.languages',
    category: 'background',
    order: 6,
    kind: 'multi-select',
    options: LANGUAGE_OPTIONS,
    multiBounds: { min: 1, max: 8 },
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip-list',
  },

  // ─── Religion ──────────────────────────────────────────────────
  {
    field: 'sect',
    labelKey: 'trait.sect',
    category: 'religion',
    order: 1,
    kind: 'select',
    options: SECT_OPTIONS,
    hardFilter: true,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'subSect',
    labelKey: 'trait.subSect',
    category: 'religion',
    order: 2,
    kind: 'select',
    options: SUB_SECT_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'religiosity',
    labelKey: 'trait.religiosity',
    helpKey: 'trait.religiosity.help',
    category: 'religion',
    order: 3,
    kind: 'range',
    range: { min: 1, max: 5 },
    hardFilter: true,
    editableAfterWizard: true,
    required: true,
    display: 'stars',
  },
  {
    field: 'prays',
    labelKey: 'trait.prays',
    category: 'religion',
    order: 4,
    kind: 'select',
    options: PRAYER_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'fastsRamadan',
    labelKey: 'trait.fastsRamadan',
    category: 'religion',
    order: 5,
    kind: 'select',
    options: FASTING_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'quranEngagement',
    labelKey: 'trait.quranEngagement',
    category: 'religion',
    order: 6,
    kind: 'select',
    options: QURAN_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'dressStyle',
    labelKey: 'trait.dressStyle',
    category: 'religion',
    order: 7,
    kind: 'select',
    // options chosen at render time based on the profile's gender
    options: [...DRESS_WOMAN_OPTIONS, ...DRESS_MAN_OPTIONS],
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'diet',
    labelKey: 'trait.diet',
    category: 'religion',
    order: 8,
    kind: 'select',
    options: DIET_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'alcohol',
    labelKey: 'trait.alcohol',
    category: 'religion',
    order: 9,
    kind: 'select',
    options: ALCOHOL_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'smoking',
    labelKey: 'trait.smoking',
    category: 'religion',
    order: 10,
    kind: 'select',
    options: SMOKING_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'convertStatus',
    labelKey: 'trait.convertStatus',
    category: 'religion',
    order: 11,
    kind: 'select',
    options: CONVERT_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },

  // ─── Family (current state) ────────────────────────────────────
  // maritalStatus is registered above under family.order=1.
  {
    field: 'hasChildren',
    labelKey: 'trait.hasChildren',
    category: 'family',
    order: 2,
    kind: 'boolean',
    hardFilter: true,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'childrenCount',
    labelKey: 'trait.childrenCount',
    category: 'family',
    order: 3,
    kind: 'number',
    range: { min: 0, max: 20 },
    hardFilter: false,
    editableAfterWizard: true,
    required: false,
    display: 'plain-text',
  },
  {
    field: 'childrenCustody',
    labelKey: 'trait.childrenCustody',
    category: 'family',
    order: 4,
    kind: 'select',
    options: CHILDREN_CUSTODY_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: false,
    display: 'chip',
  },

  // ─── Future plans ──────────────────────────────────────────────
  {
    field: 'marriageTimeframe',
    labelKey: 'trait.marriageTimeframe',
    category: 'future_plans',
    order: 1,
    kind: 'select',
    options: MARRIAGE_TIMEFRAME_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'familyInvolvement',
    labelKey: 'trait.familyInvolvement',
    category: 'future_plans',
    order: 2,
    kind: 'select',
    options: FAMILY_INVOLVEMENT_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'wantsChildren',
    labelKey: 'trait.wantsChildren',
    category: 'future_plans',
    order: 3,
    kind: 'select',
    options: WANTS_CHILDREN_OPTIONS,
    hardFilter: true,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'relocation',
    labelKey: 'trait.relocation',
    category: 'future_plans',
    order: 4,
    kind: 'select',
    options: RELOCATION_OPTIONS,
    hardFilter: true,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'livingArrangement',
    labelKey: 'trait.livingArrangement',
    category: 'future_plans',
    order: 5,
    kind: 'select',
    options: LIVING_ARRANGEMENT_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },
  {
    field: 'polygamyPreference',
    labelKey: 'trait.polygamyPreference',
    category: 'future_plans',
    order: 6,
    kind: 'select',
    options: POLYGAMY_PREFERENCE_OPTIONS,
    hardFilter: false,
    editableAfterWizard: true,
    required: true,
    display: 'chip',
  },

  // ─── Personality ───────────────────────────────────────────────
  {
    field: 'interests',
    labelKey: 'trait.interests',
    category: 'personality',
    order: 1,
    kind: 'multi-select',
    options: INTEREST_OPTIONS,
    multiBounds: { min: 0, max: 15 },
    hardFilter: false,
    editableAfterWizard: true,
    required: false,
    display: 'chip-list',
  },
  {
    field: 'personalityTraits',
    labelKey: 'trait.personalityTraits',
    category: 'personality',
    order: 2,
    kind: 'multi-select',
    options: PERSONALITY_OPTIONS,
    multiBounds: { min: 0, max: 10 },
    hardFilter: false,
    editableAfterWizard: true,
    required: false,
    display: 'chip-list',
  },

  // ─── Bio & prompts ─────────────────────────────────────────────
  {
    field: 'bio',
    labelKey: 'trait.bio',
    helpKey: 'trait.bio.help',
    category: 'bio_prompts',
    order: 1,
    kind: 'textarea',
    maxLength: 2000,
    hardFilter: false,
    editableAfterWizard: true,
    required: false,
    display: 'plain-text',
  },
  {
    field: 'promptAnswers',
    labelKey: 'trait.promptAnswers',
    helpKey: 'trait.promptAnswers.help',
    category: 'bio_prompts',
    order: 2,
    // Rendered by a bespoke component (prompt picker + textareas);
    // the registry just marks the field's existence so it surfaces
    // on the profile view + edit page.
    kind: 'textarea',
    hardFilter: false,
    editableAfterWizard: true,
    required: false,
    display: 'plain-text',
  },

  // ─── Privacy ───────────────────────────────────────────────────
  {
    field: 'blurUntilMatch',
    labelKey: 'trait.blurUntilMatch',
    category: 'privacy',
    order: 1,
    kind: 'boolean',
    hardFilter: false,
    editableAfterWizard: true,
    required: false,
    display: 'badge',
  },
  {
    field: 'paidApproachOnly',
    labelKey: 'trait.paidApproachOnly',
    category: 'privacy',
    order: 2,
    kind: 'boolean',
    hardFilter: false,
    editableAfterWizard: true,
    required: false,
    display: 'badge',
  },
] as const;

/** Lookup a trait by field name. */
export function getTrait(field: string): Trait | undefined {
  return PROFILE_TRAITS.find((t) => t.field === field);
}

/** Return all traits in a category, sorted by `order`. */
export function traitsByCategory(
  category: import('./categories.js').ProfileTraitCategory,
): Trait[] {
  return PROFILE_TRAITS.filter((t) => t.category === category).sort((a, b) => a.order - b.order);
}

/** All trait fields that the user can edit after the wizard. Used by
 *  profile-service to build the EditableProfileSchema dynamically. */
export const EDITABLE_AFTER_WIZARD_FIELDS: readonly string[] = PROFILE_TRAITS.filter(
  (t) => t.editableAfterWizard,
).map((t) => t.field);
